/**
 * @fileoverview Pending targets table of the Sales Performance screen.
 * @description Lists the open targets of the selection with the bank, its owner, the due date and
 * a status chip (Overdue, At Risk or On Track), in the order the backend gave them: most urgent
 * first. The backend caps the list and reports the total and how many are overdue, which the card
 * header shows.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { PENDING_STATUS_CHIP_CLASS } from '@/features/sales/lib/sales-performance.utils'

import TableEmptyState from './TableEmptyState'

import type { SalesPerformancePending } from '@/features/sales/types/sales-performance.types'

const GRID = 'grid-cols-[minmax(170px,2fr)_minmax(130px,1.4fr)_minmax(95px,1fr)_minmax(95px,1fr)]'
const COLUMN_KEYS = ['bank', 'owner', 'due', 'status'] as const

interface PerformancePendingTableProps {
  pending: SalesPerformancePending
}

/**
 * @component
 * @param {PerformancePendingTableProps} props - The pending list to show.
 * @param {SalesPerformancePending} props.pending - The open targets and their totals.
 */
function PerformancePendingTable({ pending }: PerformancePendingTableProps) {
  const { t } = useTranslation()

  if (pending.items.length === 0) {
    return (
      <TableEmptyState
        message={t('sales.salesPerformance.pending.emptyTitle')}
        description={t('sales.salesPerformance.pending.emptyDescription')}
        icon="check"
        tone="good"
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[620px]">
        <div
          className={`grid ${GRID} items-center gap-3 border-y border-divider bg-input-bg px-3 py-2.5`}
        >
          {COLUMN_KEYS.map((key) => (
            <span
              key={key}
              className="text-caption font-semibold tracking-caption text-table-header"
            >
              {t(`sales.salesPerformance.pending.columns.${key}`)}
            </span>
          ))}
        </div>
        {pending.items.map((item) => (
          <div
            key={item.targetId}
            className={`grid ${GRID} items-center gap-3 border-b border-row-border px-3 py-2.5`}
            data-testid="sales-performance-pending-row"
          >
            <div className="flex min-w-0 flex-col">
              <span
                className="truncate text-compact-lg font-semibold text-ink"
                data-testid="sales-performance-pending-bank"
              >
                {item.bank}
              </span>
              <span
                className="truncate text-xs text-footer"
                data-testid="sales-performance-pending-tenant-code"
              >
                {item.tenantCode}
              </span>
            </div>
            <span
              className="truncate text-compact text-cell"
              data-testid="sales-performance-pending-owner"
            >
              {item.owner.name}
            </span>
            <span
              className="text-compact tabular-nums text-cell"
              data-testid="sales-performance-pending-due-date"
            >
              {item.dueDate}
            </span>
            <span
              className={`inline-flex w-fit items-center rounded-chip px-2.5 py-1 text-xs font-semibold ${PENDING_STATUS_CHIP_CLASS[item.status]}`}
              data-testid="sales-performance-pending-status"
            >
              {t(`sales.salesPerformance.pending.statuses.${item.status}`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PerformancePendingTable
